import { useRef } from "react";
import { toast } from "sonner";
import { FileCheck2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useFetch } from "@/hooks/useFetch";
import { api, downloadFile, errMsg } from "@/lib/api";

const KINDS = [
  { type: "PO", label: "Upload PO", id: "po", accept: ".pdf,image/*,.doc,.docx,.xls,.xlsx" },
  { type: "Email Confirmation", label: "Upload email / screenshot", id: "email", accept: ".eml,.msg,.pdf,image/*" },
];

const sendFile = (orderId, type, file) => {
  const fd = new FormData();
  Object.entries({ entity_type: "order", entity_id: orderId, doc_type: type, title: "" }).forEach(([k, v]) => fd.append(k, v));
  fd.append("file", file);
  return api.post("/documents", fd);
};

export const uploadStaged = (orderId, staged) => Promise.all(Object.entries(staged).map(([t, f]) => sendFile(orderId, t, f)));

function Slot({ kind, files, onPick, required }) {
  const ref = useRef(null);
  return (
    <div className="rounded border border-dashed border-slate-300 bg-slate-50 p-3" data-testid={`lm-doc-slot-${kind.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs font-semibold text-slate-700">{kind.type === "PO" ? "Customer PO" : "Email confirmation / screenshot"}{required ? " *" : " (optional)"}</div>
        {onPick && <Button size="sm" variant="outline" onClick={() => ref.current?.click()} data-testid={`lm-upload-${kind.id}-btn`}><Upload className="h-4 w-4 mr-1" />{kind.label}</Button>}
        <input ref={ref} type="file" accept={kind.accept} className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) onPick(f); e.target.value = ""; }} data-testid={`lm-upload-${kind.id}-input`} />
      </div>
      <div className="mt-2 space-y-1">
        {files.map((d) => (
          <button key={d.key} type="button" className="flex items-center gap-1 text-xs text-sky-700 hover:underline text-left" onClick={d.open} data-testid={`lm-doc-${kind.id}-file`}><FileCheck2 className="h-3.5 w-3.5 shrink-0" />{d.name}</button>
        ))}
        {!files.length && <div className="text-xs text-slate-400">No file yet</div>}
      </div>
    </div>
  );
}

// PO and email-confirmation files for an LM: staged before the LM exists, uploaded straight away after.
export function ConfirmationDocs({ orderId, staged, setStaged, poRequired, canEdit = true }) {
  const { data, reload } = useFetch(orderId ? `/documents?entity_type=order&entity_id=${orderId}` : null);
  const pick = (type) => async (file) => {
    if (!orderId) return setStaged({ ...staged, [type]: file });
    try { await sendFile(orderId, type, file); toast.success(`${type} file uploaded`); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const filesOf = (type) => (orderId
    ? (data || []).filter((d) => d.doc_type === type).map((d) => ({ key: d.id, name: d.stored_name, open: () => downloadFile(`/documents/${d.id}/download`, d.stored_name) }))
    : staged[type] ? [{ key: type, name: `${staged[type].name} (uploads on save)`, open: () => {} }] : []);
  return (
    <div className="grid gap-3 md:grid-cols-2" data-testid="lm-confirmation-docs">
      {KINDS.map((k) => <Slot key={k.id} kind={k} files={filesOf(k.type)} onPick={canEdit ? pick(k.type) : null} required={k.type === "PO" && poRequired} />)}
    </div>
  );
}
